import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "../components/AuthProvider.js";
import { api } from "../lib/api.js";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button.js";

export const Route = createFileRoute("/episodes/new")({
  component: NewEpisodePage,
  validateSearch: (search: Record<string, unknown>) => ({
    guestId: search.guestId as string | undefined,
  }),
});

interface Guest { id: string; name: string; email: string; }

const statusLabels: Record<string, string> = { inquiry: "Inquiry", scheduled: "Scheduled", recorded: "Recorded", edited: "Edited", published: "Published", promoted: "Promoted", completed: "Completed" };

function NewEpisodePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { guestId: preselectedGuestId } = Route.useSearch();
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ 
    guestId: preselectedGuestId || "", 
    title: "", 
    number: "", 
    description: "", 
    scheduledAt: "", 
    topics: "", 
    prepNotes: "", 
    status: "inquiry" 
  });

  useEffect(() => { if (!user) return; api.get("/api/guests").then(r => r.json()).then(d => setGuests(d.guests)); }, [user]);
  if (!user) return <div>Loading...</div>;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/api/episodes", { guestId: form.guestId, title: form.title, number: form.number || undefined, description: form.description || undefined, scheduledAt: form.scheduledAt || undefined, topics: form.topics.split(",").map(t => t.trim()).filter(Boolean), prepNotes: form.prepNotes || undefined });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      if (form.status !== "inquiry") await api.patch(`/api/episodes/${data.episode.id}`, { status: form.status });
      navigate({ to: "/episodes" });
    } catch { setError("Failed to create episode."); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Link to="/episodes" className="text-sm text-muted-foreground hover:text-foreground">← Back to episodes</Link>
      <h1 className="text-2xl font-bold mt-2 mb-6">Schedule New Episode</h1>
      {error && <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-1">Guest *</label>
          <select required value={form.guestId} onChange={e => setForm({ ...form, guestId: e.target.value })} className="w-full px-3 py-2 border rounded-md">
            <option value="">Select a guest...</option>
            {guests.map(g => <option key={g.id} value={g.id}>{g.name} ({g.email})</option>)}
          </select>
          {guests.length === 0 && <p className="mt-1 text-sm text-muted-foreground">No guests yet. <Link to="/guests/new" search={{}} className="text-primary hover:underline">Add a guest first</Link>.</p>}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Episode Title *</label><input type="text" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 border rounded-md" /></div>
          <div><label className="block text-sm font-medium mb-1">Episode Number</label><input type="text" value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} className="w-full px-3 py-2 border rounded-md" /></div>
        </div>
        <div><label className="block text-sm font-medium mb-1">Description</label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border rounded-md" rows={3} /></div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Recording Date</label><input type="datetime-local" value={form.scheduledAt} onChange={e => setForm({ ...form, scheduledAt: e.target.value })} className="w-full px-3 py-2 border rounded-md" /></div>
          <div><label className="block text-sm font-medium mb-1">Current Status</label><select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 border rounded-md">{Object.entries(statusLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        </div>
        <div><label className="block text-sm font-medium mb-1">Topics</label><input type="text" value={form.topics} onChange={e => setForm({ ...form, topics: e.target.value })} className="w-full px-3 py-2 border rounded-md" placeholder="topic1, topic2, topic3" /></div>
        <div><label className="block text-sm font-medium mb-1">Prep Notes</label><textarea value={form.prepNotes} onChange={e => setForm({ ...form, prepNotes: e.target.value })} className="w-full px-3 py-2 border rounded-md" rows={4} placeholder="Questions to ask..." /></div>
        <div className="flex gap-3">
          <Button type="submit" disabled={loading || !form.guestId} className="px-6">
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Episode"
            )}
          </Button>
          <Link to="/episodes" className="px-6 py-2 border rounded-md font-medium hover:bg-muted inline-flex items-center">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
